import CancelIcon from '@mui/icons-material/Cancel';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { IconButton, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useContext } from "react";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { defaultGeographies, defaultStages, scrollbarStyle } from "../../../shared/dashboard";
import theme from '../../../theme';
import MultiSelector from "../../atoms/MultiSelector";
import { WorkspaceFilters } from './WorkspacesTableHeader';

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 'max-content',
        minWidth: 200,
        maxWidth: 400,
        height: 'fit-content',
        marginTop: 16,
        padding: '12px 16px',
        paddingRight: 24,
        overflowX: 'hidden',
        overflowY: 'auto',
        borderRadius: 12,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
        ...scrollbarStyle,
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: theme.colors.neutral['700'],
    },
    label: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    clear: {
        minWidth: 80,
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: theme.palette.warning.dark,
        textTransform: 'none !important' as any,
        transition: 'ease-in-out 300ms',
        "&:hover": {
            background: 'transparent',
        }
    },
    count: {
        minWidth: 100,
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['800'],
    },
    cropped: {
        maxWidth: 300,
        minHeight: 35,
        maxHeight: 70,
        overflowX: 'hidden',
        overflowY: 'auto',
    },
    button: {
        width: '100%',
        padding: '4px !important',
        fontFamily: 'Lato',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: '#666666',
        textTransform: 'none',
        justifyContent: 'flex-start',
        "& > .MuiButton-startIcon": {
            "& > .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                color: theme.palette.primary.main,
            }
        },
    },
}));

const WorkspacesTableFilter: React.FC<{
    filters?: WorkspaceFilters,
    filterType?: string,
    readOnly?: boolean,
    onFilter?: ({ sectors, stages, geography } : WorkspaceFilters) => void,
}> = ({ filters, filterType, readOnly, onFilter }) => {
    const classes = useStyles();
    const { tags } = useContext(GroupSettingsContext);

    return (<>
        <Stack className={classes.card} spacing={1} alignItems="flex-start" justifyContent="flex-start" width="100%">
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start" width="100%">
                <Stack direction="row" spacing="4px" alignItems="center" justifyContent="flex-start" minWidth={92} mt="4px">
                    <FilterAltOutlinedIcon sx={{ width: 24, height: 24, color: theme.palette.primary.main }} />
                    <Typography className={classes.title}>{'View by:'}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                    {filterType === 'Sectors' && (<>
                        <MultiSelector
                            mainOptions={tags}
                            selections={filters?.sectors}
                            onSelection={(selections) => {
                                onFilter?.({ sectors: selections });
                            }}
                            readOnly={readOnly}
                            fullSpan
                            withSearch />
                        {(!readOnly && !!filters?.sectors?.length) && (
                            <IconButton size="small"
                                onClick={() => {
                                    onFilter?.({ sectors: [] });
                                }}
                                sx={{ color: theme.palette.warning.dark }}
                                disableRipple>
                                <CancelIcon sx={{ width: 28, height: 28, }} />
                            </IconButton>
                        )}
                    </>)}
                    {filterType === 'Stage' && (<>
                        <MultiSelector
                            mainOptions={defaultStages}
                            selections={filters?.stages}
                            onSelection={(selections) => {
                                onFilter?.({ stages: selections });
                            }}
                            readOnly={readOnly}
                            fullSpan
                            withSearch />
                        {(!readOnly && !!filters?.stages?.length) && (
                            <IconButton size="small"
                                onClick={() => {
                                    onFilter?.({ stages: [] });
                                }}
                                sx={{ color: theme.palette.warning.dark }}
                                disableRipple>
                                <CancelIcon sx={{ width: 28, height: 28, }} />
                            </IconButton>
                        )}
                    </>)}
                    {filterType === 'Geography' && (<>
                        <MultiSelector
                            mainOptions={defaultGeographies}
                            selections={filters?.geography}
                            onSelection={(selections) => {
                                onFilter?.({ geography: selections })
                            }}
                            readOnly={readOnly}
                            fullSpan
                            withSearch />
                        {(!readOnly && !!filters?.geography?.length) && (
                            <IconButton size="small"
                                onClick={() => {
                                    onFilter?.({ geography: [] })
                                }}
                                sx={{ color: theme.palette.warning.dark }}
                                disableRipple>
                                <CancelIcon sx={{ width: 28, height: 28, }} />
                            </IconButton>
                        )}
                    </>)}
                </Stack>
            </Stack>
        </Stack>
    </>);
}

export default WorkspacesTableFilter;